import { Tooltip } from '@material-ui/core';
import moment from 'moment';
import React, { forwardRef, useEffect, useState } from 'react';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { connect } from 'react-redux';
import { Button, IconClickable, Text, View } from 'src/components/common';
import { IRootState } from 'src/redux/rootReducer';
import { isEmpty } from 'src/validations';
import './styles.scss';

type DateRange = [Date, Date];

const DateRangePicker: React.FC<Props> = ({
  onChange,
  dateFormat = 'MM/DD/YYYY',
  jumpRange = 'week',
  selecteds,
  rangeSelectable = false,
  allowToJump = true,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dates, setDates] = useState<DateRange>(selecteds as DateRange);

  useEffect(() => {
    setDates(selecteds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecteds]);

  const handleDateChange = (dates: DateRange) => {
    setDates(dates);
    if (!!dates[0] && !!dates[1]) {
      setIsOpen(false);
      onChange(dates);
    }
  };

  const fomartDates = (): string => {
    if (isEmpty(selecteds)) return '';

    return selecteds?.map(x => moment(x).format(dateFormat as string)).join(' - ') || '';
  };

  const handleClickOutside = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setIsOpen(false);
  };

  const handlePrevClick = () => {
    if (jumpRange === 'week') {
      handleJumpPrevWeek();
    } else {
      handleJumpPrevMonth();
    }
  };

  const handleNextClick = () => {
    if (jumpRange === 'week') {
      handleJumpNextWeek();
    } else {
      handleJumpNextMonth();
    }
  };

  const handleJumpPrevWeek = () => {
    const startDate = moment(dates[0]).subtract(7, 'days').startOf('week');
    const endDate = moment(startDate).endOf('week');
    handleDateChange([startDate.toDate(), endDate.toDate()]);
  };

  const handleJumpNextWeek = () => {
    const startDate = moment(dates[0]).add(7, 'days').startOf('week');
    const endDate = moment(startDate).endOf('week');
    handleDateChange([startDate.toDate(), endDate.toDate()]);
  };

  const handleJumpPrevMonth = () => {
    const endDate = moment(dates[0]).subtract(1, 'day');
    const startDate = moment(endDate).startOf('month');
    handleDateChange([startDate.toDate(), endDate.toDate()]);
  };

  const handleJumpNextMonth = () => {
    const startDate = moment(dates[1]).add(1, 'day');
    const endDate = moment(startDate).endOf('month');

    handleDateChange([startDate.toDate(), endDate.toDate()]);
  };

  // For more information:
  // https://reactdatepicker.com/

  const CustomInput = forwardRef(({ value, onClick }: any, ref: any) => (
    <View isRow align="center">
      {allowToJump && (
        <Tooltip title={`Prev ${jumpRange === 'week' ? 'Week' : 'Month'}`}>
          <IconClickable
            name="ic_chevron-left"
            className={'ctn-timesheet__change-week-icon mr-8'}
            onClick={handlePrevClick}
          />
        </Tooltip>
      )}
      {rangeSelectable ? (
        <Button
          className="custom-daterange__value"
          variant={'text-secondary'}
          label={value}
          onClick={onClick}
          ref={ref}
        />
      ) : (
        <Text>{value}</Text>
      )}

      {allowToJump && (
        <Tooltip title={`Next ${jumpRange === 'week' ? 'Week' : 'Month'}`}>
          <IconClickable
            name="ic_chevron-right"
            className={'ctn-timesheet__change-week-icon ml-8'}
            onClick={handleNextClick}
          />
        </Tooltip>
      )}
    </View>
  ));

  const startDate = dates?.[0];
  const endDate = dates?.[1];
  return (
    <DatePicker
      onChange={handleDateChange}
      showPopperArrow={false}
      {...props}
      customInput={<CustomInput />}
      value={fomartDates()}
      selectsRange
      selected={startDate}
      startDate={startDate}
      endDate={endDate}
      open={isOpen}
      onInputClick={() => rangeSelectable && setIsOpen(true)}
      onClickOutside={handleClickOutside}
    />
  );
};

type Props = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps &
  ReactDatePickerProps & {
    onChange?: (value: DateRange) => void;
    selecteds?: [Date, Date];
    jumpRange?: 'week' | 'month';
    rangeSelectable?: boolean;
    allowToJump?: boolean;
  };

const mapStateToProps = (state: IRootState) => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(DateRangePicker);
